/**
 * Cute Objects picture-tier vignettes (AUTHORING.md v3): one painted indoor background per picture — the sleepover
 * house (public/vignettes/cuteObjects/<bg>.webp, prompts docs/vignette-prompts.py, CREDITS.md) + the picture's posed friend
 * from core (`content/worlds/cuteObjects/pictures.ts`). `elements` = every part the painting shows (walls / corner, floor or
 * table top, lamp glow and moonlight included: each one is drawn + coloured in the picture lesson); `overlay` = story
 * props the painting lacks, drawn in vector (Nana's tiny mouse guest, the lost socks, the candle glow).
 */
import type { VignetteSpec, WorldVignettes } from '../../vignette/types';

const v = (id: string, elements: string[], overlay?: string[]): VignetteSpec => ({ id: `${id}-picture`, bg: id, elements, ...(overlay ? { overlay } : {}) });

const specs: VignetteSpec[] = [
  v('cute-cupcake', ['wall', 'floor', 'window', 'shelf']),
  v('cute-donut', ['wall', 'floor', 'window', 'door', 'mat']),
  v('cute-remix-cupcake-candle', ['wall', 'floor', 'window', 'tiles', 'vase'], ['glow']),
  v('cute-remix-cupcake-cat-ears', ['wall', 'floor', 'window', 'curtains', 'plant']),
  v('cute-remix-cupcake-rainbow', ['wall', 'floor', 'window', 'tiles', 'dome']),
  v('cute-macaron', ['wall', 'floor', 'window', 'bowl', 'box']),
  v('cute-ice-cream', ['wall', 'floor', 'window', 'tiles', 'fan', 'fridge']),
  v('cute-teacup', ['wall', 'floor', 'window', 'cushions', 'blanket']),
  v('cute-teapot', ['wall', 'floor', 'window', 'doily', 'rack'], ['mouse']),
  v('cute-remix-ice-cream-popsicle', ['wall', 'floor', 'window', 'tiles', 'cactus']),
  v('cute-remix-ice-cream-triple', ['wall', 'floor', 'window', 'bunting', 'wafers', 'bowls']),
  v('cute-remix-ice-cream-sundae', ['wall', 'floor', 'window', 'awning', 'cherries', 'napkins']),
  v('cute-strawberry', ['wall', 'floor', 'window', 'bed', 'pillow']),
  v('cute-toast', ['wall', 'floor', 'window', 'tiles', 'toaster', 'breadBin']),
  v('cute-pancake-stack', ['wall', 'floor', 'window', 'cloth', 'jug']),
  v('cute-remix-toast-egg', ['wall', 'floor', 'window', 'eggCup', 'salt']),
  v('cute-remix-toast-jam-smile', ['wall', 'floor', 'window', 'jarsL', 'jarsR']),
  v('cute-remix-toast-butter-blanket', ['wall', 'floor', 'window', 'tiles', 'breadBin']),
  v('cute-remix-strawberry-tutu', ['wall', 'floor', 'window', 'bar', 'mirror']),
  v('cute-remix-strawberry-half', ['wall', 'floor', 'glow', 'window', 'bedside', 'bed', 'rug']),
  v('cute-remix-strawberry-trio', ['wall', 'floor', 'window', 'bench', 'balls']),
  v('cute-gift-box', ['wall', 'floor', 'glow', 'fairy', 'plant', 'rug']),
  v('cute-backpack', ['wall', 'floor', 'glow', 'desk', 'door']),
  v('cute-teddy-bear', ['wall', 'floor', 'moonlight', 'window', 'books'], ['socks']),
  v('cute-remix-gift-box-jack', ['wall', 'floor', 'glow', 'window', 'lamp', 'toys']),
  v('cute-remix-gift-box-stack', ['wall', 'floor', 'glow', 'window', 'fireplace', 'logs', 'rug']),
  v('cute-remix-gift-box-round', ['wall', 'floor', 'glow', 'window', 'nightLight', 'ladder', 'rug']),
  v('cute-remix-backpack-frog', ['wall', 'floor', 'window', 'boots', 'umbrella']),
  v('cute-remix-backpack-bear', ['wall', 'floor', 'glow', 'window', 'clock', 'lamp', 'bed'], ['socks']),
  v('cute-remix-backpack-camping', ['wall', 'floor', 'window', 'paperMoon', 'tent']),
];

export const CUTE_VIGNETTES: WorldVignettes = { world: 'cuteObjects', specs };
